import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

/** Persist only the open/closed state for this panel */
const OPEN_KEY = "EmitterTool:SpritePanelOpenV1";

function isTextureMap(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const vals = Object.values(obj);
  return vals.length > 0 && vals.every(
    (v) => v && typeof v === "object" && ("baseTexture" in v || "valid" in v)
  );
}

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch {}
  }, [open]);

  return (
    <div style={{
      border: "1px solid #334155",
      borderRadius: 8,
      marginBottom: 12,
      overflow: "hidden",
      background: "#0b0f17",
      maxWidth: "100%",
      boxSizing: "border-box",
    }}>
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && (
        <div style={{ padding: 8, boxSizing: "border-box", maxWidth: "100%", overflowX: "hidden" }}>
          {children}
        </div>
      )}
    </div>
  );
}

const labelStyle = { fontSize: 12, opacity: 0.85 };
const inputStyle = {
  width: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  boxSizing: "border-box",
};
const sliderStyle = { width: "100%", accentColor: "#64748b", cursor: "pointer" };

function Badge({ text }) {
  return (
    <div style={{
      position: "absolute", top: 6, left: 6, minWidth: 20, height: 20, padding: "0 6px",
      borderRadius: 999, background: "#1f2937", border: "1px solid #60a5fa", color: "#e5e7eb",
      fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
    }}>
      {text}
    </div>
  );
}

function SpriteThumb({ texture, size = 72 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!texture) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const frame =
      texture.frame ||
      texture.orig || { x: 0, y: 0, width: texture.width, height: texture.height };

    const source =
      texture.baseTexture?.resource?.source ||
      texture.baseTexture?.resource?.sourceHTML ||
      texture.baseTexture?.resource?.src ||
      texture.baseTexture?.resource?.url ||
      texture.baseTexture?.resource?.sourceElement ||
      texture.baseTexture?.resource?.bitmap ||
      null;

    const ctx = canvas.getContext("2d");
    const w = Math.max(1, frame.width || texture.width || 1);
    const h = Math.max(1, frame.height || texture.height || 1);

    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const cell = 6;
    for (let y = 0; y < size; y += cell) {
      for (let x = 0; x < size; x += cell) {
        ctx.fillStyle = (Math.floor(x / cell + y / cell) % 2 === 0) ? "#0e141f" : "#101826";
        ctx.fillRect(x, y, cell, cell);
      }
    }

    if (!source || (!source.width && !source.videoWidth)) return;

    const scale = Math.min(size / w, size / h);
    const drawW = Math.max(1, Math.floor(w * scale));
    const drawH = Math.max(1, Math.floor(h * scale));
    const dx = Math.floor((size - drawW) / 2);
    const dy = Math.floor((size - drawH) / 2);

    ctx.imageSmoothingEnabled = false;
    try {
      ctx.drawImage(source, frame.x || 0, frame.y || 0, w, h, dx, dy, drawW, drawH);
    } catch {}
  }, [texture, size]);

  return <canvas ref={canvasRef} width={size} height={size} style={{ width: size, height: size, borderRadius: 6, display: "block", background: "#0f172a" }} />;
}

/**
 * Controlled SpritePanel:
 * - Now supports an explicit mode via props/local to allow choosing Multi/Animated from empty state.
 * - If `spriteMode` prop is provided, it wins. Else we use a local `localMode` if set.
 * - If neither is set, we derive from arrays (anim > multi > single).
 */
export default function SpritePanel({
  atlas,

  // selection states
  selectedSprite,
  setSelectedSprite,
  selectedSprites,
  setSelectedSprites,
  onMultiChange,
  maxSelected = Number.POSITIVE_INFINITY,

  selectedAnimFrames,
  setSelectedAnimFrames,
  animFps,
  setAnimFps,
  onAnimatedChange,
  maxAnimFrames = 24,

  // NEW: explicit mode control (optional)
  spriteMode,              // "single" | "multi" | "anim" (optional controlled)
  onModeChange,            // (mode) => void

  // panel appearance
  title = "Sprites",
  defaultOpen = true,

  // effect settings passthrough (optional)
  setEffectSettings,
}) {
  // normalize atlas
  const { textures, frameNames } = useMemo(() => {
    if (!atlas) return { textures: {}, frameNames: [] };
    if (atlas.textures) {
      const names = Array.isArray(atlas.frameNames)
        ? atlas.frameNames.slice()
        : Object.keys(atlas.textures || {});
      return { textures: atlas.textures || {}, frameNames: names };
    }
    if (isTextureMap(atlas)) {
      return { textures: atlas, frameNames: Object.keys(atlas) };
    }
    return { textures: {}, frameNames: [] };
  }, [atlas]);

  // lists and helpers
  const ensureA = (a) => (Array.isArray(a) ? a : []);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  const activeMulti = ensureA(selectedSprites);
  const activeAnim  = ensureA(selectedAnimFrames);

  // derived mode from arrays
  const derivedMode = useMemo(() => {
    if (activeAnim.length >= 2) return "anim";
    if (activeMulti.length > 1)  return "multi";
    return "single";
  }, [activeMulti.length, activeAnim.length]);

  // local mode if the parent doesn't control it
  const [localMode, setLocalMode] = useState(undefined);

  // the mode we actually use
  const mode = spriteMode || localMode || derivedMode;

  // keep localMode cleared if parent starts controlling mode
  useEffect(() => {
    if (spriteMode && localMode && spriteMode !== localMode) {
      setLocalMode(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spriteMode]);

  // search and UI state (not persisted)
  const [filter, setFilter] = useState("");
  const [showNames, setShowNames] = useState(true);
  const [thumbSize, setThumbSize] = useState(72);

  // fps UI mirror (props are authoritative)
  const activeAnimFps = Number(animFps || 12);

  // all/filtered names
  const allNames = useMemo(() => {
    const base = frameNames.length ? frameNames.slice() : Object.keys(textures || {});
    const cleaned = base.filter((n) => typeof n === "string" && n.length > 0);
    if (!frameNames.length) cleaned.sort((a, b) => a.localeCompare(b));
    return cleaned;
  }, [frameNames.join("|"), Object.keys(textures || {}).join("|")]);

  const filteredNames = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return allNames;
    return allNames.filter((name) => name.toLowerCase().includes(q));
  }, [allNames, filter]);

  // commit helpers
  const emitMode = (next) => {
    onModeChange?.(next);
    try { window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetMode", { detail: { mode: next } })); } catch {}
  };

  const setModeExplicit = (next) => {
    // prefer parent control if available
    if (typeof setEffectSettings === "function") {
      setEffectSettings((es) => {
        const base = { ...(es || {}) };

        if (next === "single") {
          base.spriteMode = "single";
          base.selectedAnimFrames = [];
          base.spriteAnimatedEnabled = false;
          const first = selectedSprite || (ensureA(selectedSprites)[0] || null);
          base.selectedSprites = first ? [first] : [];
        } else if (next === "multi") {
          base.spriteMode = "multi";
          base.selectedAnimFrames = [];
          base.spriteAnimatedEnabled = false;
          base.selectedSprites = ensureA(selectedSprites);
        } else if (next === "anim") {
          base.spriteMode = "anim";
          base.spriteAnimatedEnabled = ensureA(selectedAnimFrames).length >= 2;
          base.selectedAnimFrames = ensureA(selectedAnimFrames);
          if (!Number.isFinite(+base.animFps)) base.animFps = 12;
        }
        return base;
      });
    }

    // set local mode so the grid starts using the right click handler immediately
    setLocalMode(next);
    emitMode(next);
  };

  const commitMulti = (next) => {
    const safe = ensureA(next);
    setSelectedSprites?.(safe);

    if (typeof setEffectSettings === "function") {
      setEffectSettings((es) => ({
        ...(es || {}),
        selectedSprites: safe.slice(),
        spriteMode: "multi",
        spriteAnimatedEnabled: false,
        selectedAnimFrames: [],
      }));
    }

    if (safe.length && typeof setSelectedSprite === "function") {
      setSelectedSprite(safe[0] || safe[safe.length - 1]);
    }

    onMultiChange?.(safe);

    // remain in multi explicitly if parent isn't controlling it
    if (!spriteMode) setLocalMode("multi");

    try { window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetMulti", { detail: { names: safe } })); } catch {}
  };

  const commitAnimFrames = (next) => {
    const safe = ensureA(next);
    setSelectedAnimFrames?.(safe);

    if (typeof setEffectSettings === "function") {
      setEffectSettings((es) => ({
        ...(es || {}),
        selectedAnimFrames: safe.slice(),
        spriteMode: "anim",
        spriteAnimatedEnabled: safe.length >= 2,
      }));
    }

    if (safe.length && typeof setSelectedSprite === "function") {
      setSelectedSprite(safe[0] || safe[safe.length - 1]);
    }

    onAnimatedChange?.(safe, activeAnimFps);

    // remain in anim explicitly if parent isn't controlling it
    if (!spriteMode) setLocalMode("anim");

    try { window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetAnim", { detail: { frames: safe, fps: activeAnimFps } })); } catch {}
  };

  const commitAnimFps = (fps) => {
    const clamped = clamp(Number(fps) || 1, 1, 120);
    setAnimFps?.(clamped);

    if (typeof setEffectSettings === "function") {
      setEffectSettings((es) => ({ ...(es || {}), animFps: clamped }));
    }

    onAnimatedChange?.(activeAnim, clamped);

    try { window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetAnim", { detail: { frames: activeAnim.slice(), fps: clamped } })); } catch {}
  };

  // click handlers
  const handleClickSingle = (name) => {
    setSelectedSprite?.(name);

    if (typeof setEffectSettings === "function") {
      setEffectSettings((es) => ({
        ...(es || {}),
        spriteMode: "single",
        selectedSprite: name,
        selectedSprites: [name],
        selectedAnimFrames: [],
        spriteAnimatedEnabled: false,
      }));
    }

    if (!spriteMode) setLocalMode("single");

    try { window.dispatchEvent(new CustomEvent("EmitterTool:SpritePanel:SetSingle", { detail: { name } })); } catch {}
  };

  const isFiniteMax = Number.isFinite(maxSelected);

  const handleClickMulti = (name) => {
    const arr = activeMulti.slice();
    const idx = arr.indexOf(name);
    if (idx >= 0) { arr.splice(idx, 1); commitMulti(arr); return; }
    if (isFiniteMax) {
      if (arr.length < maxSelected) arr.push(name);
      else arr[arr.length - 1] = name;
    } else {
      arr.push(name);
    }
    commitMulti(arr);
  };

  const handleClickAnim = (name) => {
    const arr = activeAnim.slice();
    const idx = arr.indexOf(name);
    if (idx >= 0) { arr.splice(idx, 1); commitAnimFrames(arr); return; }
    if (arr.length < maxAnimFrames) arr.push(name);
    else arr[arr.length - 1] = name;
    commitAnimFrames(arr);
  };

  // reorder & clear
  const moveUp = (i) => {
    if (mode === "multi") {
      if (i <= 0) return;
      const arr = activeMulti.slice();
      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
      commitMulti(arr);
    } else if (mode === "anim") {
      if (i <= 0) return;
      const arr = activeAnim.slice();
      [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
      commitAnimFrames(arr);
    }
  };
  const moveDown = (i) => {
    if (mode === "multi") {
      if (i >= activeMulti.length - 1) return;
      const arr = activeMulti.slice();
      [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
      commitMulti(arr);
    } else if (mode === "anim") {
      if (i >= activeAnim.length - 1) return;
      const arr = activeAnim.slice();
      [arr[i + 1], arr[i]] = [arr[i], arr[i + 1]];
      commitAnimFrames(arr);
    }
  };
  const clearAll = () => {
    if (mode === "multi") commitMulti([]);
    else if (mode === "anim") commitAnimFrames([]);
  };

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      {/* Top controls */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 8, alignItems: "center", boxSizing: "border-box", maxWidth: "100%",
      }}>
        <input
          type="text"
          placeholder="Search sprites…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={inputStyle}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0, boxSizing: "border-box" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={showNames} onChange={(e) => setShowNames(e.target.checked)} style={{ accentColor: "#64748b" }} />
            <span style={{ fontSize: 12 }}>Show names</span>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
            <span style={{ fontSize: 12, opacity: 0.8, whiteSpace: "nowrap" }}>Mode:</span>
            <button
              onClick={() => setModeExplicit("single")}
              style={{
                background: mode === "single" ? "#0b1220" : "transparent",
                border: mode === "single" ? "2px solid #60a5fa" : "1px solid #3b475c",
                color: "white", borderRadius: 6, padding: "6px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}
              title="Pick exactly one sprite"
            >
              Single
            </button>
            <button
              onClick={() => setModeExplicit("multi")}
              style={{
                background: mode === "multi" ? "#0b1220" : "transparent",
                border: mode === "multi" ? "2px solid #60a5fa" : "1px solid #3b475c",
                color: "white", borderRadius: 6, padding: "6px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}
              title="Pick multiple sprites for variety"
            >
              Multi
            </button>
            <button
              onClick={() => setModeExplicit("anim")}
              style={{
                background: mode === "anim" ? "#0b1220" : "transparent",
                border: mode === "anim" ? "2px solid #60a5fa" : "1px solid #3b475c",
                color: "white", borderRadius: 6, padding: "6px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
              }}
              title="Build an ordered list of frames and set FPS"
            >
              Animated
            </button>
            {(mode === "multi" || mode === "anim") && (
              <button
                onClick={clearAll}
                style={{
                  background: "transparent",
                  border: "1px solid #3b475c",
                  color: "white", borderRadius: 6, padding: "6px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap",
                }}
                title="Clear selection"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Thumb size */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ ...labelStyle, display: "flex", justifyContent: "space-between" }}>
          <span>Thumbnail size</span>
          <span style={{ opacity: 0.7 }}>{thumbSize}px</span>
        </div>
        <input type="range" min={32} max={160} step={1} value={thumbSize} onChange={(e) => setThumbSize(Number(e.target.value))} style={sliderStyle} />
      </div>

      {/* Animated FPS controls */}
      {mode === "anim" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr minmax(64px, 96px)", gap: 8, marginBottom: 10, alignItems: "center", boxSizing: "border-box", maxWidth: "100%" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ ...labelStyle, display: "flex", justifyContent: "space-between" }}>
              <span>Frames per second</span>
              <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{activeAnimFps} fps</span>
            </div>
            <input type="range" min={1} max={120} step={1} value={activeAnimFps} onChange={(e) => commitAnimFps(Number(e.target.value))} style={sliderStyle} />
          </div>
          <input type="number" min={1} max={120} step={1} value={activeAnimFps} onChange={(e) => commitAnimFps(Number(e.target.value))} style={inputStyle} />
        </div>
      )}

      {/* Summary */}
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 8, overflowWrap: "anywhere", wordBreak: "break-word" }}>
        {Object.keys(textures || {}).length
          ? `${filteredNames.length} / ${(frameNames.length ? frameNames.length : Object.keys(textures || {}).length)} sprites`
          : "Spritesheet not loaded"} — {
            mode === "single" ? "single-select" :
            mode === "multi"  ? (Number.isFinite(maxSelected) ? `multi-select (max ${maxSelected})` : "multi-select (no limit)") :
                                `animated frames (max ${maxAnimFrames})`
        }
      </div>

      {/* Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${thumbSize + 16}px, 1fr))`,
        gap: 8, maxHeight: 360, overflow: "auto", overflowX: "hidden", paddingRight: 2, boxSizing: "border-box", maxWidth: "100%",
      }}>
        {filteredNames.map((name) => {
          const tex = textures[name];
          const isSelected =
            mode === "single" ? (selectedSprite === name)
            : mode === "multi" ? activeMulti.includes(name)
            : /* anim */         activeAnim.includes(name);

          const orderBadge =
            mode === "multi" ? (activeMulti.indexOf(name) + 1)
            : mode === "anim"  ? (activeAnim.indexOf(name) + 1)
            : 0;

          const onClick =
            mode === "single" ? () => handleClickSingle(name)
            : mode === "multi" ? () => handleClickMulti(name)
            : () => handleClickAnim(name);

          return (
            <button
              key={name}
              title={name}
              onClick={onClick}
              style={{
                position: "relative",
                border: isSelected ? "2px solid #60a5fa" : "1px solid #374151",
                outline: "none",
                background: isSelected ? "#0b1220" : "transparent",
                borderRadius: 8,
                padding: 6,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                alignItems: "center",
                cursor: "pointer",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {isSelected && (mode === "multi" || mode === "anim") && orderBadge > 0 ? (
                <Badge text={String(orderBadge)} />
              ) : null}
              <SpriteThumb texture={tex} size={thumbSize} />
              {showNames && (
                <div style={{ width: "100%", fontSize: 11, lineHeight: 1.2, textAlign: "center", color: "#cbd5e1", wordBreak: "break-word" }}>
                  {name}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected info */}
      {mode === "single" && selectedSprite && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #334155", fontSize: 12, color: "#cbd5e1" }}>
          <div style={{ marginBottom: 4, fontWeight: 600 }}>Selected</div>
          <div style={{ opacity: 0.85 }}>{selectedSprite}</div>
        </div>
      )}

      {mode === "multi" && activeMulti.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #334155", fontSize: 12, color: "#cbd5e1", display: "grid", gap: 6, boxSizing: "border-box", maxWidth: "100%" }}>
          <div style={{ marginBottom: 2, fontWeight: 600 }}>
            Selected ({activeMulti.length}{Number.isFinite(maxSelected) ? ` / ${maxSelected}` : ""})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {activeMulti.map((n, i) => (
              <div key={`${n}:${i}`} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ opacity: 0.75, width: 22, textAlign: "center", flex: "0 0 auto" }}>{i + 1}</span>
                <span style={{ opacity: 0.95, flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{n}</span>
                <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
                  <button onClick={() => moveUp(i)}   style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: i > 0 ? "pointer" : "not-allowed", opacity: i > 0 ? 1 : 0.5 }}>↑</button>
                  <button onClick={() => moveDown(i)} style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: i < activeMulti.length - 1 ? "pointer" : "not-allowed", opacity: i < activeMulti.length - 1 ? 1 : 0.5 }}>↓</button>
                  <button onClick={() => { const arr = activeMulti.slice(); arr.splice(i, 1); commitMulti(arr); }} style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: "pointer" }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "anim" && activeAnim.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #334155", fontSize: 12, color: "#cbd5e1", display: "grid", gap: 6, boxSizing: "border-box", maxWidth: "100%" }}>
          <div style={{ marginBottom: 2, fontWeight: 600 }}>
            Frames ({activeAnim.length}{maxAnimFrames ? ` / ${maxAnimFrames}` : ""}) — {activeAnimFps} fps
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {activeAnim.map((n, i) => (
              <div key={`${n}:${i}`} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ opacity: 0.75, width: 60, textAlign: "center", flex: "0 0 auto", whiteSpace: "nowrap" }}>Frame {i + 1}</span>
                <span style={{ opacity: 0.95, flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{n}</span>
                <div style={{ display: "flex", gap: 6, marginLeft: "auto", flexWrap: "wrap" }}>
                  <button onClick={() => moveUp(i)}   style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: i > 0 ? "pointer" : "not-allowed", opacity: i > 0 ? 1 : 0.5 }}>↑</button>
                  <button onClick={() => moveDown(i)} style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: i < activeAnim.length - 1 ? "pointer" : "not-allowed", opacity: i < activeAnim.length - 1 ? 1 : 0.5 }}>↓</button>
                  <button onClick={() => { const arr = activeAnim.slice(); arr.splice(i, 1); commitAnimFrames(arr); }} style={{ background: "transparent", border: "1px solid #3b475c", color: "white", borderRadius: 6, padding: "2px 6px", fontSize: 11, cursor: "pointer" }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleBox>
  );
}

export { SpritePanel };
